import { View, Pressable } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Controller, useForm } from "react-hook-form"
import { toast } from "burnt"
import { zodResolver } from "@hookform/resolvers/zod"
import { router, useLocalSearchParams } from "expo-router"
import { KeyboardAwareScrollView } from "react-native-keyboard-controller"

import { createSupplierPaymentInput } from "@white-labels/input/src/m5azen"

import { trpc, dayjs, sharedStyles } from "@src/utils"
import { ar } from "@src/i18n/ar"
import { analytics } from "@src/utils/analytics"
import { Button, Text, TextInput, Header, DatePicker, PriceInput, SupplierPicker } from "@src/components"

const BALANCE_TYPES = ["cash", "visa", "wallet"] as const

const resolver = zodResolver(createSupplierPaymentInput)
export default function CreatePaymentScreen() {
  const { supplierId: supplierIdParam } = useLocalSearchParams<{ supplierId?: string }>()
  const initialSupplierId = supplierIdParam ? Number.parseInt(supplierIdParam, 10) : undefined
  const utils = trpc.useUtils()
  const createPayment = trpc.payments.create.useMutation()

  const { control, formState, handleSubmit } = useForm({
    resolver,
    defaultValues: { supplierId: initialSupplierId, balanceType: "cash" as const, date: dayjs().unix() },
  })

  const loading = createPayment.isPending
  const disabled = loading || !formState.isValid

  async function onSubmit(data: Parameters<typeof createPayment.mutateAsync>[0]) {
    try {
      await createPayment.mutateAsync(data)
      utils.balance.get.invalidate()
      utils.payments.list.invalidate()
      utils.suppliers.get.invalidate({ id: data.supplierId })
      utils.balance.track.invalidate({ type: data.balanceType })

      analytics.logEvent({ name: "payment_created", params: { amount: data.amount, method: data.balanceType } })
      router.back()
    } catch (error) {
      toast({ title: ar.error, message: (error as Error).message, preset: "error" })
    }
  }

  return (
    <>
      <Header title={ar.addPayment} />
      <KeyboardAwareScrollView className="bg-white flex-1" contentContainerStyle={sharedStyles.contentContainer}>
        <Text className="text-sm font-semibold text-gray-700 mb-2">
          {ar.supplier} <Text className="text-red-500">*</Text>
        </Text>
        <Controller
          control={control}
          name="supplierId"
          render={({ field }) => (
            <SupplierPicker className="mb-4" value={field.value ? [field.value] : []} onSelectionChange={(ids) => field.onChange(ids[0])} />
          )}
        />

        <Text className="text-sm font-semibold text-gray-700 mb-2">
          {ar.amount} <Text className="text-red-500">*</Text>
        </Text>
        <View className="relative mb-4">
          <View className="absolute top-0 bottom-0 end-0 z-10 px-3 justify-center pointer-events-none">
            <Text className="text-gray-400 font-medium">{ar.egp}</Text>
          </View>
          <Controller
            control={control}
            name="amount"
            render={({ field }) => <PriceInput value={field.value ? Number(field.value) : undefined} onValueChange={field.onChange} />}
          />
        </View>

        <Text className="text-sm font-semibold text-gray-700 mb-2">
          {ar.paymentSource} <Text className="text-red-500">*</Text>
        </Text>
        <Controller
          control={control}
          name="balanceType"
          render={({ field }) => (
            <View className="flex-row gap-2 mb-4">
              {BALANCE_TYPES.map((type) => (
                <Pressable
                  key={type}
                  onPress={() => field.onChange(type)}
                  className={`flex-1 py-2.5 items-center rounded-lg border ${field.value === type ? "bg-blue-600 border-blue-600" : "bg-white border-gray-200"}`}
                >
                  <Text className={`text-sm font-semibold ${field.value === type ? "text-white" : "text-gray-700"}`}>{ar[type]}</Text>
                </Pressable>
              ))}
            </View>
          )}
        />

        <Text className="text-sm font-semibold text-gray-700 mb-2">{ar.paymentDate}</Text>
        <Controller
          name="date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={field.value ? dayjs.unix(field.value).toDate() : undefined}
              onChange={(date) => field.onChange(date ? dayjs(date).unix() : undefined)}
              placeholder={ar.paymentDate}
              mode="datetime"
              className="mb-4"
            />
          )}
        />

        <Text className="text-sm font-semibold text-gray-700 mb-2">{ar.notes}</Text>
        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <TextInput value={field.value} onChangeText={field.onChange} placeholder={ar.addNotesAboutPayment} multiline numberOfLines={4} className="mb-6" />
          )}
        />

        <Button disabled={disabled} loading={loading} onPress={handleSubmit(onSubmit)}>
          {ar.confirm}
        </Button>
        <SafeAreaView edges={["bottom"]} />
      </KeyboardAwareScrollView>
    </>
  )
}
